import {
	CreateActivityInfoBody,
	OnFailureAlarm,
	PutActivityInfoBody,
} from '@amzn/aws-hammerstone-exposed-restful-service-typescript-client/clients/hammerstoneexposedrestfulservicelambda';
import configMetadataPostprocess from './configMetadataPostprocessor';
import keys from '../../constants/hammerstoneConstantKeys';
import Activity from 'src/interfaces/activityInterfaces';
import { completeDatetimeRe } from 'src/components/helpers/content/Rules';
import { BulkUpdateFormValues, FieldGroupRecord, BlankFieldPolicy } from 'src/interfaces/bulkUpdateInterfaces';

function onFailureAlarmPostprocessor(activityForm: Activity): OnFailureAlarm {
	const { category, type, item, ...formAlarm } = activityForm.alarm;
	const isCtiComplete = [category, type, item].every((value) => value?.trim());
	return {
		...formAlarm,
		// If any of the CTI fields are blank/empty, remove all the fields
		...(isCtiComplete ? { category, type, item } : {}),
		active: activityForm.alarm.active ? 'true' : 'false',
	};
}

export const activityFormToUpdateBody = (activityForm: Activity, alias: string): PutActivityInfoBody => {
	const updateBody: Partial<PutActivityInfoBody> = {
		updatedBy: alias,
		operation: keys.ActivityActions.update,
	};

	activityDefaultPostprocessor(updateBody, activityForm);
	updateBody['comment'] = activityForm.comment;

	return updateBody as PutActivityInfoBody;
};

export function activityFormToCreateBody(activityForm: Activity, alias: string): CreateActivityInfoBody {
	const createBody: Partial<CreateActivityInfoBody> = {
		createdBy: alias,
		activityName: activityForm.activityName,
	};

	activityDefaultPostprocessor(createBody, activityForm);
	createBody.activityNamespace = activityForm.activityNamespace;

	// Only add ETL activity type if creating a new activity, since API disallows updates to activity type
	createBody.configurationMetadata.keyValuesList.push({ key: 'ETL_LOAD_TYPE', value: activityForm.activityType });

	return createBody as CreateActivityInfoBody;
}

/** Adds and postprocesses the default keys for both creating and editing an activity. Operation happens in-place on the body object, and also returns a pointer. */
function activityDefaultPostprocessor<BodyType extends PutActivityInfoBody | CreateActivityInfoBody>(
	body: Partial<BodyType>,
	activityForm: Activity
): BodyType {
	// String keys
	body.activityName = activityForm.activityName;

	const [matched, time] = activityForm.scheduleDate.match(completeDatetimeRe) ?? [];
	// If the time is left blank, set it to a default of midnight UTC
	body.scheduleDate = time ? matched : `${matched} 00:00:00`;

	body.scheduleType = activityForm.scheduleType;
	body.clusterName = activityForm.clusterName;
	body.s3PreconRegion = activityForm.s3PreconRegion;

	// Boolean keys (explicitly converted to avoid undefined)
	body.activityRetryActive = activityForm.activityRetryActive ? true : false;
	body.activityRetryPause = activityForm.activityRetryPause ? true : false;

	// Number Keys, make sure they are all ints (no dec)
	// In case the schedule type is set to only run once, automatically set the schedule interval to 1.
	body.scheduleInterval =
		body.scheduleType === keys.ScheduleType.ONETIME ? 1 : Math.floor(activityForm.scheduleInterval);
	body.activityPriority = Math.floor(activityForm.activityPriority);
	body.scheduleDateOffset = Math.floor(activityForm.scheduleDateOffset);
	body.activityRetryDelay = Math.floor(activityForm.activityRetryDelay);
	body.activityRetryMaximum = Math.floor(activityForm.activityRetryMaximum);

	// Preconditions, mapping keys and values to prevent extraneous props from being added to the object (i.e. useFieldArray adds 'id')
	body.preconditions = activityForm.preconditions.map(({ key, value }) => ({ key, value }));

	body['configurationMetadata'] = configMetadataPostprocess(activityForm);

	// onFailureAlarm from alarm
	body.onFailureAlarm = onFailureAlarmPostprocessor(activityForm);
	return body as BodyType;
}

/**
 * Builds the PutActivityInfo API request body from the bulk edit form values.
 * Only includes fields for the selected (checked) field groups, plus the required
 * `operation` and `updatedBy` fields.
 *
 * @param formValues - The current form values from react-hook-form
 * @param selectedFieldGroups - Which field groups are checked
 * @param alias - The user's alias for the `updatedBy` field
 * @returns A partial PutActivityInfoBody containing only the relevant fields
 */
export function buildBulkUpdatePayload(
	formValues: BulkUpdateFormValues,
	selectedFieldGroups: FieldGroupRecord,
	alias: string
): { body: PutActivityInfoBody; bulkConfigKeyValues: Record<string, string> | undefined } {
	const body: PutActivityInfoBody = {
		operation: 'update',
		updatedBy: alias,
	};
	const keyValues: Record<string, string> = {};

	if (selectedFieldGroups.schedule) {
		body.scheduleDate = formValues.scheduleDate;
		body.scheduleInterval = formValues.scheduleInterval;
		body.scheduleType = formValues.scheduleType;
	}

	if (selectedFieldGroups.retry) {
		body.activityRetryActive = formValues.activityRetryActive ? true : false;
		body.activityRetryMaximum = Math.floor(formValues.activityRetryMaximum);
		body.activityRetryDelay = Math.floor(formValues.activityRetryDelay);
		body.activityRetryPause = formValues.activityRetryPause ? true : false;
	}

	if (selectedFieldGroups.alarm) {
		const onFailureAlarm: PutActivityInfoBody['onFailureAlarm'] = {
			// `active` always overwrites — toggles have no natural "blank" state.
			active: formValues.alarm.active ? 'true' : 'false',
		};

		// Severity and string fields honor the blank-field policy: under KEEP, a blank value is
		// omitted from the request so the activity's existing value is preserved. Note that under
		// OVERWRITE we still omit a blank severity (the API has no "cleared" severity value), so
		// in practice the policy only matters for severity when paired with a non-blank value.
		const overwriteBlanks = formValues.blankFieldPolicy?.alarm === BlankFieldPolicy.OVERWRITE;
		if (formValues.alarm.severity !== undefined && formValues.alarm.severity !== null) {
			onFailureAlarm.severity = formValues.alarm.severity;
		}
		const stringFields = ['dedupe', 'login', 'category', 'type', 'item'] as const;
		stringFields.forEach((field) => {
			const value = formValues.alarm[field];
			if (value || overwriteBlanks) {
				onFailureAlarm[field] = value;
			}
		});

		body.onFailureAlarm = onFailureAlarm;
		keyValues['DJS_ALARM_ALL_EXECUTIONS_FAILED'] = formValues.config.DJS_ALARM_ALL_EXECUTIONS_FAILED ? 'Y' : 'N';
		keyValues['DJS_ALARM_EXECUTION_FAILURE'] = formValues.config.DJS_ALARM_EXECUTION_FAILURE ? 'Y' : 'N';
		keyValues['DJS_ALARM_DISABLED_JOB'] = formValues.config.DJS_ALARM_DISABLED_JOB ? 'Y' : 'N';
	}

	if (selectedFieldGroups.cluster) {
		body.clusterName = formValues.clusterName;
	}

	if (selectedFieldGroups.iamRole) {
		const iamRoles = formValues.config?.IAM_ROLES ?? [];
		const [primaryRole, ...chainRoles] = iamRoles;

		keyValues['IAM_ROLE_ARN'] = primaryRole?.value;
		keyValues['IAM_CHAIN_ROLES'] = chainRoles.length ? ',' + chainRoles.map((r) => r.value).join(',') : '';
	}

	const keyValuesList = Object.entries(keyValues);

	return { body, bulkConfigKeyValues: keyValuesList.length ? keyValues : undefined };
}
